import { fetchAuthSession } from "@aws-amplify/auth";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SessionManager from "../../services/session-manager";
import { getUser } from "../../services/auth";
import { useAuthStore } from "../../store/useAuthStore";
import { useUserStore } from "../../store/useUserStore";
import { useToast } from "../../hooks/useToast";
import Loader from "../../components/Loader";
import { getWorkspaceByRole } from "../../utils/portal";

export default function CallbackPage() {
  const [isLoading, setIsLoading] = useState(false);
  const hasRun = useRef(false); 
  const setRole = useUserStore((state) => state.setRole);
  const { loginAuth, setEmail, setUserId, setDriverId, setUserName, setFirstName, setLastName } =
    useAuthStore();
  const navigate = useNavigate();
  const { showInfoToast } = useToast();

  useEffect(() => {
    if (hasRun.current) return;   // <-- prevent double execution
    hasRun.current = true;
    const handleRedirect = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();
        const idTokenPayload = session.tokens?.idToken?.payload as any;
        const idToken: any = session.tokens?.idToken?.toString();

        if (!accessToken) throw new Error("No access token");
        const sessionManager = new SessionManager();
        await sessionManager.setAccessToken(accessToken);
        await getUserDetails(idToken, idTokenPayload?.email);
      } catch (err) {
        console.error("Error handling callback", err);
        failSignIn(
          "Sign-in could not be completed. Please try again.",
          false,
        );
      }
    };

    handleRedirect();
  }, [navigate]);

  // Never strand the user on a blank /claims. Say what failed and put them
  // back on a screen they can act from.
  const failSignIn = (message: string, rejected: boolean) => {
    console.error("Sign-in callback failed:", message);
    showInfoToast(message);
    setIsLoading(false);

    if (rejected) {
      // The account itself was refused — tear the half-session down and
      // land on the logged-out screen, which explains the state.
      window.location.href = "/logged-out";
      return;
    }

    // Transient/server-side failure: back to the login screen to retry.
    let portal = "claims";
    try {
      portal = localStorage.getItem("selectedPortal") || "claims";
    } catch {
      portal = "claims";
    }
    navigate(`/admin/login?portal=${portal}`, { replace: true });
  };

  const getUserDetails = async (IdToken: string, cognitoEmail?: string) => {
    setIsLoading(true);
    const response = await getUser(IdToken);
    if (response?.status === 200) {
      setUserId(response.data.userId);
      setRole(response.data.role);
      localStorage.setItem("selectedPortal", getWorkspaceByRole(response.data.role));
      setEmail(response.data.email || cognitoEmail || "");
      setDriverId(response.data.driverId || "");
      setUserName(response.data.user_name);
      setFirstName(response.data.first_name);
      setLastName(response.data.last_name);
      loginAuth();
      navigate("/dashboard");
      setIsLoading(false);
    } else {
      // response.data was read unguarded here: on a 5xx or a network error
      // it is undefined, so this threw and the outer catch silently swallowed
      // it — leaving a blank page and no clue what went wrong.
      const status = response?.status;
      const rejected = status === 401 || status === 403;
      const message =
        response?.data?.message ||
        (status
          ? `Sign-in could not be completed (server returned ${status}).`
          : "Sign-in could not be completed - the server did not respond.");
      failSignIn(message, rejected);
    }
  };

  return <p>{isLoading ? <Loader isLoading={isLoading} /> : null}</p>;
}
